import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useVT } from 'virtualizedtableforantd4';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import Highlighter from 'react-highlight-words';
import { tz } from 'moment-timezone';
import {
  Space,
  Tag,
  Table,
  Button,
  Input,
  Select,
  PageHeader,
  Row,
  Col,
  DatePicker,
  Spin,
  message as messageAntd,
  Statistic,
  Card,
  Modal,
  Alert,
  Comment,
  Result,
  Tooltip,
  Avatar,
} from 'antd';
import {
  LoadingOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import {
  getDataInventarioRDS,
  downloadCsvData,
} from '../actions/inventariords';
import {
  currency,
  formatterMetrico,
  onlyTwoDigits,
} from '../helpers/formatter';

function InvRDS(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const csvLink = useRef();
  const timerSearch = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [registros, setRegistros] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const loading = useSelector((state) => state.inventarioRDSMxl.loading);
  const data = useSelector((state) => state.inventarioRDSMxl.data);
  const [filterData, setFilterData] = useState(null);
  const csvData = useSelector((state) => state.reports.csvData);
  const csvName = useSelector((state) => state.reports.csvName);
  const columnsData = useSelector(
    (state) => state.inventarioRDSMxl.columnsData
  );

  const [vt] = useVT(
    () => ({
      scroll: { y: '70vh' },
    }),
    [data]
  );
  useEffect(() => {
    setFilteredInfo({});
    setSearchText('');
    setFilterData(null);
    setRegistros(null);
    dispatch(getDataInventarioRDS());
    return () => clearTimeout(timerSearch);
  }, []);

  function downloadCSV() {
    dispatch(downloadCsvData()).then(() => {
      csvLink.current.link.click();
    });
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {}}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        <div>{text}</div>
      ),
  });

  const distinct = (key) => {
    let unique = [];
    if (filterData) {
      if (filterData.length > 0) {
        unique = [...new Set(filterData.map((item) => item[key]))];
      }
    } else if (registros) {
      if (registros.length > 0) {
        unique = [...new Set(registros.map((item) => item[key]))];
      }
    } else {
      unique = [...new Set(data.map((item) => item[key]))];
    }

    let filters = [];
    for (let i = 0; i < unique.length; i++) {
      filters.push({ text: unique[i], value: unique[i] });
    }
    return filters;
  };

  const createColumns = (
    columnsData,
    data,
    filteredInfo,
    filterData,
    registros
  ) => {
    if (!data) {
      return null;
    }

    let _columns = [];

    for (let i = 0; i < columnsData.length; i++) {
      let item = columnsData[i];
      let column = {};

      column.dataIndex = item.index;

      if (item.width !== 0) {
        column.minWidth = item.width;
        column.width = item.width;
      }
      if (item.fixed !== '') {
        column.fixed = item.fixed;
      }

      switch (item.tipo) {
        case 'total':
          let total = 0;
          if (filterData) {
            if (filterData.length > 0) {
              total = filterData.length;
            }
          } else if (registros) {
            if (registros.length > 0) {
              total = registros.length;
            }
          } else {
            total = data.length;
          }
          column.title = () => {
            return (
              <div style={{ fontWeight: '700' }}>
                {item.nombre}
                <div style={{ color: 'slategray', fontWeight: '400' }}>
                  ({total})
                </div>
              </div>
            );
          };
          column.className = 'm-nowrap large';
          break;
        case 'sumCurrency':
        case 'sum':
          let sum = 0;
          if (filterData) {
            if (filterData.length > 0) {
              sum = filterData
                .map((i) => i[item.index])
                .reduce((prev, next) => prev + next);
            }
          } else if (registros) {
            if (registros.length > 0) {
              sum = registros
                .map((i) => i[item.index])
                .reduce((prev, next) => prev + next);
            }
          } else if (data.length > 0) {
            sum = data
              .map((i) => i[item.index])
              .reduce((prev, next) => prev + next);
          }
          sum =
            item.tipo === 'sumCurrency'
              ? currency.format(sum, 2)
              : onlyTwoDigits.format(sum);
          column.title = () => {
            return (
              <div style={{ fontWeight: '700' }}>
                {item.nombre}
                <div style={{ color: 'slategray', fontWeight: '400' }}>
                  ({sum})
                </div>
              </div>
            );
          };
          column.className = 'm-nowrap large';
          break;
        default:
          column.title = () => {
            return <div style={{ fontWeight: '700' }}>{item.nombre}</div>;
          };
          column.className = 'm-nowrap';
          break;
      }

      switch (item.sorter) {
        case 'number':
          column.sorter = (a, b) => a[item.index] - b[item.index];
          break;
        case 'date':
          column.render = (text) => (
            <div> {moment(text, 'YYYY-MM-DD').format('DD/MM/YYYY')} </div>
          );
          column.sorter = (a, b) =>
            moment(a[item.index]).unix() - moment(b[item.index]).unix();
        default:
          break;
      }

      switch (item.filtro) {
        case 'distinct':
          column.filters = distinct(item.index);
          column.onFilter = (value, record) =>
            record[item.index].indexOf(value) === 0;
          break;
        case 'search':
          column = { ...column, ...getColumnSearchProps(item.index) };
        default:
          break;
      }

      switch (item.formato) {
        case 'currency':
          column.render = (text) => <div> {currency.format(text)} </div>;
          break;
        default:
          break;
      }

      switch (item.index) {
        case 'total':
          break;
        default:
          break;
      }

      column.filteredValue = filteredInfo[item.index] || null;

      _columns.push(column);
    }

    return _columns;
  };

  const columns = useMemo(
    () => createColumns(columnsData, data, filteredInfo, filterData, registros),
    [filterData, columnsData, filteredInfo]
  );

  function onSearch(value) {
    clearInterval(timerSearch.current);
    timerSearch.current = setTimeout(() => {
      const filter = data.filter((o) =>
        Object.keys(o).some((k) =>
          String(o[k]).toLowerCase().includes(value.toLowerCase())
        )
      );
      value === '' ? setFilterData(null) : setFilterData(filter);
      setRegistros(null);
      setFilteredInfo({});
    }, 600);
  }
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const handleTableChange = (pagination, filters, sorters, extra) => {
    setRegistros(extra.currentDataSource);
    setFilteredInfo(filters);
  };

  return (
    <div style={{ margin: '18px' }}>
      <Spin
        spinning={loading}
        tip="Loading..."
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        <PageHeader
          className="m-page-header"
          style={{ paddingBottom: '20px' }}
          onBack={null}
          title={
            <div style={{ fontSize: '18px' }}>
              Inventario RDS Mxli | Grupo Optima{' '}
            </div>
          }
          subTitle={null}
          extra={[
            <Button
              key="0"
              shape="circle"
              type="primary"
              onClick={downloadCSV}
              style={{ background: 'green', borderColor: 'green' }}
            >
              <FileExcelOutlined />
            </Button>,
            <CSVLink
              style={{ display: 'none' }}
              ref={csvLink}
              target="_blank"
              filename={csvName}
              key="1"
              data={csvData}
            ></CSVLink>,
            <input
              key="2"
              style={{
                border: '1px solid lightslategray',
                borderRadius: '3px',
              }}
              placeholder=" Buscar..."
              type="search"
              onChange={(e) => onSearch(e.target.value)}
            />,
          ]}
        />
      </Spin>
      <div>
        <font style={{ color: 'black' }}>Registros:</font>
        {filterData ? filterData.length : data ? data.length : 0}
      </div>

      <div>
        <Table
          tableLayout="auto"
          pagination={false}
          scroll={{ y: '70vh' }}
          style={{ marginTop: '32px' }}
          size="small"
          components={vt}
          columns={columns}
          onChange={handleTableChange}
          dataSource={filterData ? filterData : data ? data : []}
        />
      </div>
    </div>
  );
}

export default InvRDS;
